// Symmetric ("mirror") drawing. While a mirror axis is on, every cell a draw
// gesture touches is also applied to its reflection across the canvas center on
// that axis — so a shape drawn on one side appears mirrored on the other.
//
// The active config is module-scoped, like the `interaction` state: main sets it
// when a sketch gesture begins (from the symmetry toggles + the current canvas
// bounds), and every shape handler funnels its grid writes through mirrorCells()
// at the point it touches a cell. Because the mirrors are expanded at that single
// point, the handlers' own retraction bookkeeping (box/circle `applied`, extrude
// per-layer cells, voxel `placedCells`) tracks the mirror cells too, so shrinking
// or undoing a stroke peels both sides in lockstep.

export interface MirrorConfig {
  x: boolean;
  y: boolean;
  z: boolean;
  // Per-axis reflection constant: cell coord c maps to (sum - c). Set to
  // (min + max - 1) for a volume whose cells span [min, max-1] so a cell and its
  // mirror are paired symmetrically about the canvas center and stay in bounds.
  sumX: number;
  sumY: number;
  sumZ: number;
}

type Cell = { ix: number; iy: number; iz: number };

let active: MirrorConfig | null = null;

// Set (or clear) the active mirror config for the next gesture. A config with no
// axis enabled is treated as off, so callers don't have to special-case it.
export function setMirror(cfg: MirrorConfig | null): void {
  active = cfg && (cfg.x || cfg.y || cfg.z) ? cfg : null;
}

// True when any mirror axis is on. Lets hot per-cell loops take an alloc-free
// fast path (write the one cell directly) instead of calling mirrorCells, which
// allocates an array + cell object per call even in the common no-mirror case.
export function isMirrorActive(): boolean {
  return active !== null;
}

// Every cell a draw should touch for one primary cell: the cell itself plus its
// reflection across each enabled axis (2/4/8-fold), deduplicated. Returns just
// [the cell] when no mirror is active, so callers always iterate the result.
// Inclusive cell box (min/max per axis), matching box.ts's AABB.
export interface BoxAABB {
  x0: number; x1: number; y0: number; y1: number; z0: number; z1: number;
}

// The box plus its 2/4/8-fold reflections across the active mirror axes — the
// AABB-level analogue of mirrorCells, so a BULK box fill can mirror without
// going cell by cell. Identity (just [box]) when no axis is active. A box on a
// mirror plane yields a coincident reflection; callers fill empties only, so the
// harmless overlap needs no dedup.
export function mirrorBoxes(box: BoxAABB): BoxAABB[] {
  if (!active) return [box];
  const reflect = (b: BoxAABB, axis: "x" | "y" | "z", sum: number): BoxAABB =>
    axis === "x"
      ? { ...b, x0: sum - b.x1, x1: sum - b.x0 }
      : axis === "y"
        ? { ...b, y0: sum - b.y1, y1: sum - b.y0 }
        : { ...b, z0: sum - b.z1, z1: sum - b.z0 };
  let out = [box];
  if (active.x) out = out.flatMap((b) => [b, reflect(b, "x", active!.sumX)]);
  if (active.y) out = out.flatMap((b) => [b, reflect(b, "y", active!.sumY)]);
  if (active.z) out = out.flatMap((b) => [b, reflect(b, "z", active!.sumZ)]);
  return out;
}

export function mirrorCells(ix: number, iy: number, iz: number): Cell[] {
  if (!active) return [{ ix, iy, iz }];
  const xs = active.x ? [ix, active.sumX - ix] : [ix];
  const ys = active.y ? [iy, active.sumY - iy] : [iy];
  const zs = active.z ? [iz, active.sumZ - iz] : [iz];
  if (xs.length === 1 && ys.length === 1 && zs.length === 1) {
    return [{ ix, iy, iz }];
  }
  const out: Cell[] = [];
  const seen = new Set<string>();
  for (const x of xs) {
    for (const y of ys) {
      for (const z of zs) {
        const k = `${x},${y},${z}`;
        if (seen.has(k)) continue; // a coord on the mirror plane maps to itself
        seen.add(k);
        out.push({ ix: x, iy: y, iz: z });
      }
    }
  }
  return out;
}
